import { useState, useEffect } from 'react'
import type { TenantBootstrapModel } from '@unified-lk/tenant-config'
import { staffApi } from '../../../../api/staff'
import type { AuthSession } from '../../../../types/plans'
import { parseDecimalInput } from '../constructorTypes'
import type { ConstructorPlan } from '../constructorTypes'
import { CashOfferForm } from '../shared/CashOfferForm'
import type { CashOfferFormFields } from '../shared/CashOfferForm'

export function PlanCashOfferPanel({
  plan,
  bootstrap,
  session,
  onReload,
}: {
  plan: ConstructorPlan
  bootstrap: TenantBootstrapModel
  session: AuthSession
  onReload: () => void
}) {
  const initial = plan.plan_cash_offer ?? null
  const [expanded, setExpanded] = useState(initial !== null)
  const [fields, setFields] = useState<CashOfferFormFields>({
    amount: initial?.amount != null ? String(initial.amount) : '',
    deadline: initial?.deadline ?? '',
    method: initial?.method ?? '',
    note: initial?.note ?? '',
  })
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    const o = plan.plan_cash_offer ?? null
    setExpanded(o !== null)
    setFields({
      amount: o?.amount != null ? String(o.amount) : '',
      deadline: o?.deadline ?? '',
      method: o?.method ?? '',
      note: o?.note ?? '',
    })
  }, [plan.plan_cash_offer])

  async function handleSave() {
    setSaving(true)
    const amountNum = fields.amount.trim() !== '' ? parseDecimalInput(fields.amount) : null
    const payload = (amountNum == null && fields.note.trim() === '' && fields.deadline.trim() === '')
      ? null
      : { amount: amountNum, note: fields.note.trim() || null, deadline: fields.deadline.trim() || null, method: fields.method.trim() || null }
    try {
      await staffApi.updateConstructorPlan(bootstrap, session, plan.id, { plan_cash_offer: payload })
      onReload()
    } catch {
      // ignore
    } finally {
      setSaving(false)
    }
  }

  async function handleClear() {
    setSaving(true)
    setFields({ amount: '', deadline: '', method: '', note: '' })
    try {
      await staffApi.updateConstructorPlan(bootstrap, session, plan.id, { plan_cash_offer: null })
      onReload()
    } catch {
      // ignore
    } finally {
      setSaving(false)
    }
  }

  const hasSaved = plan.plan_cash_offer != null && (plan.plan_cash_offer.amount != null || plan.plan_cash_offer.note != null)

  return (
    <div className="rounded-xl border border-green-200 bg-green-50/60 p-4 shadow-sm">
      <div className="flex items-center justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold text-slate-800">Цена за наличные по всему плану</h3>
          <p className="mt-1 text-[11px] text-slate-500">
            Если задана — отображается блоком на публичной странице. Перекрывает агрегат по этапам.
          </p>
        </div>
        <button
          type="button"
          className="text-xs text-slate-500 underline"
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? 'Скрыть' : 'Добавить'}
        </button>
      </div>
      {!expanded && plan.plan_cash_offer && (plan.plan_cash_offer.amount != null || plan.plan_cash_offer.note) ? (
        <p className="mt-2 text-xs text-green-800">
          {plan.plan_cash_offer.amount != null ? `${Number(plan.plan_cash_offer.amount).toLocaleString('ru-RU')} ₽` : null}
          {plan.plan_cash_offer.deadline ? ` · до ${plan.plan_cash_offer.deadline}` : ''}
          {plan.plan_cash_offer.note ? ` · ${plan.plan_cash_offer.note}` : ''}
        </p>
      ) : null}
      {expanded ? (
        <CashOfferForm
          fields={fields}
          saving={saving}
          hasSavedOffer={hasSaved}
          variant="plan"
          onFieldChange={(patch) => setFields((prev) => ({ ...prev, ...patch }))}
          onSave={() => void handleSave()}
          onClear={() => void handleClear()}
        />
      ) : null}
    </div>
  )
}
